import React, { useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import { Button } from "@mui/material"
import DateRange from "@/components/partials/DateRange"
import moment from "moment"
import { runScenario, buildRecommendation } from "@/_helpers/scenarioAnalysis"

const OBJECT_TYPES = [
  { value: "part", labelKey: "analytics.scenarioObjectTypePart" },
  { value: "service", labelKey: "analytics.scenarioObjectTypeService" },
  { value: "technician", labelKey: "analytics.scenarioObjectTypeTechnician" },
]

const SCENARIO_TYPES = [
  { value: "demand", labelKey: "analytics.scenarioDemandChange" },
  { value: "price", labelKey: "analytics.scenarioPriceChange" },
  { value: "resource", labelKey: "analytics.scenarioResourceChange" },
]

const FORECAST_MONTHS = 6

const INITIAL_FILTERS = {
  object_type: "part",
  object_id: "",
  scenario_type: "demand",
  change_percentage: 0,
  date_from: null,
  date_to: null,
}

function buildApiUrl(filters) {
  const { object_type, object_id, date_from, date_to } = filters
  const datePart = [
    date_from ? `&date_from=${date_from}` : "",
    date_to ? `&date_to=${date_to}` : "",
  ].join("")

  if (object_type === "part")
    return `analytics/part-usage-prediction?product_id=${object_id}${datePart}`
  if (object_type === "service")
    return `analytics/repair-requests?category_id=${object_id}${datePart}`
  return `analytics/technicians-workload?technician_id=${object_id}${datePart}`
}

function extractHistorical(objectType, data) {
  if (objectType === "part")
    return data.chart_data?.usage_counts_historical || []
  return data.chart_data?.historical || []
}

function extractRawLabels(data) {
  return data.chart_data?.labels || []
}

// Some endpoints return YYYY-MM (monthly), others DD.MM (daily)
function formatLabel(raw) {
  if (!raw) return ""
  if (/^\d{4}-\d{2}$/.test(raw)) {
    const m = moment(raw, "YYYY-MM")
    return m.isSame(moment(), "month")
      ? trans("analytics.today")
      : m.format("MM.YYYY")
  }
  return raw
}

// Parse a raw API label back to a moment, handling both YYYY-MM and DD.MM formats
function parseLabelToMoment(raw) {
  if (!raw) return moment()
  if (/^\d{4}-\d{2}$/.test(raw)) return moment(raw, "YYYY-MM")
  if (/^\d{2}\.\d{2}$/.test(raw)) {
    const [day, month] = raw.split(".")
    let m = moment({
      year: moment().year(),
      month: parseInt(month, 10) - 1,
      date: parseInt(day, 10),
    })
    // If the parsed date is in the future it belongs to the previous year
    if (m.isAfter(moment())) m.subtract(1, "year")
    return m
  }
  return moment()
}

function buildChartSeries(historicalData, forecastValues, allLabels) {
  const boundaryIndex = historicalData.length - 1

  const histSeries = allLabels.map((_, i) =>
    i < historicalData.length ? (historicalData[i] ?? null) : null,
  )

  const foreSeries = allLabels.map((_, i) => {
    if (i === boundaryIndex) return historicalData[boundaryIndex] ?? null
    if (i > boundaryIndex)
      return forecastValues[i - historicalData.length] ?? null
    return null
  })

  return { histSeries, foreSeries }
}

function OrdersInTime() {
  const [objectsLoading, setObjectsLoading] = useState(false)
  const [simulating, setSimulating] = useState(false)
  const [objects, setObjects] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const [chartLabels, setChartLabels] = useState([])
  const [histSeries, setHistSeries] = useState([])
  const [foreSeries, setForeSeries] = useState([])
  const [simResult, setSimResult] = useState(null)

  useEffect(() => {
    setObjects([])
    setFilters((prev) => ({ ...prev, object_id: "" }))
    loadObjects(filters.object_type)
  }, [filters.object_type])

  function loadObjects(type) {
    setObjectsLoading(true)
    const url = buildApiUrl({ ...INITIAL_FILTERS, object_type: type })
    Api.get(url)
      .then((res) => {
        const keyMap = {
          part: "products",
          service: "categories",
          technician: "technicians",
        }
        setObjects(res.data[keyMap[type]] || [])
        setObjectsLoading(false)
      })
      .catch(() => setObjectsLoading(false))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function handleRunSimulation() {
    setSimulating(true)
    setSimResult(null)

    Api.get(buildApiUrl(filters))
      .then((res) => {
        const historicalData = extractHistorical(filters.object_type, res.data)
        const rawLabels = extractRawLabels(res.data)

        // Slice raw labels to match actual historical data — the API may return
        // additional labels for its own server-side predictions beyond our data
        const histRawLabels = rawLabels.slice(0, historicalData.length)
        const historicalLabels = histRawLabels.map(formatLabel)

        const {
          baseline,
          forecastValues,
          forecastRevenue,
          requiredTechnicians,
        } = runScenario({
          historicalData,
          scenarioType: filters.scenario_type,
          changePercentage: Number(filters.change_percentage),
          forecastMonths: FORECAST_MONTHS,
        })

        const lastRaw = histRawLabels[histRawLabels.length - 1] || ""
        const isMonthly = /^\d{4}-\d{2}$/.test(lastRaw)
        const lastMoment = parseLabelToMoment(lastRaw)

        // For daily-data endpoints: fill the gap between the last historical day
        // and date_to so the full selected month appears on the chart
        const gapLabels = []
        const gapValues = []
        if (!isMonthly && filters.date_to && histRawLabels.length > 0) {
          const dateToMoment = moment(filters.date_to, "YYYY-MM-DD")
          const daysToFill = dateToMoment.diff(lastMoment, "days")
          const dailyValue = forecastValues[0] ?? 0
          for (let d = 1; d <= daysToFill; d++) {
            gapLabels.push(lastMoment.clone().add(d, "days").format("DD.MM"))
            gapValues.push(dailyValue)
          }
        }

        // Monthly forecasts anchored after the full selected period
        const monthlyAnchor =
          gapLabels.length > 0
            ? moment(filters.date_to, "YYYY-MM-DD")
            : lastMoment
        const monthlyLabels = Array.from({ length: FORECAST_MONTHS }, (_, i) =>
          monthlyAnchor
            .clone()
            .add(i + 1, "months")
            .format("MM.YYYY"),
        )

        const allForecastLabels = [...gapLabels, ...monthlyLabels]
        const allForecastValues = [...gapValues, ...forecastValues]
        const allLabels = [...historicalLabels, ...allForecastLabels]

        const { histSeries: hs, foreSeries: fs } = buildChartSeries(
          historicalData,
          allForecastValues,
          allLabels,
        )

        setChartLabels(allLabels)
        setHistSeries(hs)
        setForeSeries(fs)
        setSimResult({
          baseline,
          forecastValues,
          forecastRevenue,
          requiredTechnicians,
        })
        setSimulating(false)
      })
      .catch(() => setSimulating(false))
  }

  const forecastValue = simResult?.forecastValues?.[0] ?? 0
  const baseline = simResult?.baseline ?? 0
  const diffPct =
    baseline !== 0
      ? (((forecastValue - baseline) / baseline) * 100).toFixed(1)
      : "0.0"
  const diffPositive = parseFloat(diffPct) >= 0

  const recommendation =
    simResult !== null
      ? buildRecommendation({
          scenarioType: filters.scenario_type,
          changePercentage: Number(filters.change_percentage),
          baseline,
          forecastValue,
          forecastRevenue: simResult.forecastRevenue,
          requiredTechnicians: simResult.requiredTechnicians,
        })
      : ""

  const hasChartData = simResult !== null && chartLabels.length > 0

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>{trans("analytics.ordersInTime")}</h3>
      </div>
      <hr />

      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "flex-end",
          marginBottom: "20px",
        }}
      >
        <div style={filterColStyle}>
          <label>{trans("filters.category")}</label>
          <select
            value={filters.object_type}
            onChange={(e) => handleFilterChange("object_type", e.target.value)}
          >
            {OBJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {trans(t.labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioObject")}</label>
          {objectsLoading ? (
            <Skeleton width={160} height={38} />
          ) : (
            <select
              value={filters.object_id}
              onChange={(e) => handleFilterChange("object_id", e.target.value)}
            >
              <option value="">{trans("random.all")}</option>
              {objects.map((obj) => (
                <option key={obj.id} value={obj.id}>
                  {obj.name || obj.full_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioType")}</label>
          <select
            value={filters.scenario_type}
            onChange={(e) =>
              handleFilterChange("scenario_type", e.target.value)
            }
          >
            {SCENARIO_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {trans(t.labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.changePercentage")}</label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "38px",
            }}
          >
            <input
              type="range"
              min="-50"
              max="50"
              step="1"
              value={filters.change_percentage}
              onChange={(e) =>
                handleFilterChange("change_percentage", e.target.value)
              }
              style={{
                width: "120px",
                cursor: "pointer",
                accentColor: "#181F67",
              }}
            />
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                minWidth: "42px",
                color:
                  filters.change_percentage > 0
                    ? "#3DAA77"
                    : filters.change_percentage < 0
                      ? "#FF718B"
                      : "#888",
              }}
            >
              {filters.change_percentage > 0 ? "+" : ""}
              {filters.change_percentage}%
            </span>
          </div>
        </div>

        <div style={filterColStyle}>
          <label>{trans("analytics.scenarioPeriod")}</label>
          <DateRange
            startDate={filters.date_from || ""}
            endDate={filters.date_to || ""}
            onStartDateChange={(date) => handleFilterChange("date_from", date)}
            onEndDateChange={(date) => handleFilterChange("date_to", date)}
          />
        </div>

        <button
          onClick={handleRunSimulation}
          disabled={simulating}
          //   style={{
          //     height: "38px",
          //     backgroundColor: "#181F67",
          //     color: "#fff",
          //     textTransform: "none",
          //     fontWeight: 600,
          //     paddingLeft: 20,
          //     paddingRight: 20,
          //     flexShrink: 0,
          //   }}
          //
          style={{
            height: "40px",
          }}
          className="button button-primary"
        >
          {simulating ? "..." : trans("analytics.runSimulation")}
        </button>
      </div>

      {/* Chart */}
      {simulating ? (
        <Skeleton height={295} />
      ) : !hasChartData ? (
        <div className="alert warning" style={{ marginBottom: 0, width: "100%" }}>
          {trans("analytics.scenarioRunPrompt")}
        </div>
      ) : (
        <>
          <LineChart
            xAxis={[{ scaleType: "point", data: chartLabels }]}
            series={[
              {
                id: "sa-hist",
                curve: "natural",
                label: trans("analytics.historicalData"),
                area: true,
                data: histSeries,
                color: "#181F67",
                valueFormatter: (v) => (v == null ? "" : String(Math.round(v))),
              },
              {
                id: "sa-fore",
                curve: "natural",
                label: trans("analytics.forecast"),
                area: true,
                data: foreSeries,
                color: "#181F67",
                valueFormatter: (v) => (v == null ? "" : String(Math.round(v))),
              },
            ]}
            height={295}
            sx={{
              "& .MuiLineElement-series-sa-fore": { strokeDasharray: "6 3" },
              "& .MuiAreaElement-series-sa-hist": {
                fill: "url(#sa-hist-gradient)",
              },
              "& .MuiAreaElement-series-sa-fore": {
                fill: "url(#sa-fore-gradient)",
              },
              "& .MuiLineElement-root": { strokeWidth: 1.5 },
            }}
            slotProps={{ legend: { hidden: true } }}
          >
            <defs>
              <linearGradient id="sa-hist-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#181F67" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="sa-fore-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#181F67" stopOpacity={0} />
                <stop offset="100%" stopColor="#181F67" stopOpacity={0} />
              </linearGradient>
            </defs>
          </LineChart>

          {/* Results summary */}
          <div
            style={{
              display: "flex",
              gap: "0",
              marginTop: "16px",
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid #eef0f6",
            }}
          >
            <ResultCard
              label={trans("analytics.currentValue")}
              value={Math.round(baseline)}
              color="#181F67"
            />
            <ResultCard
              label={trans("analytics.forecastValue")}
              value={Math.round(forecastValue)}
              color="#3DAA77"
              divider
            />
            <ResultCard
              label={trans("analytics.difference")}
              value={`${diffPositive ? "+" : ""}${diffPct}%`}
              color={diffPositive ? "#3DAA77" : "#FF718B"}
              divider
            />
            {simResult.forecastRevenue !== null && (
              <ResultCard
                label={trans("analytics.forecastRevenue")}
                value={Math.round(simResult.forecastRevenue)}
                color="#181F67"
                divider
              />
            )}
            {simResult.requiredTechnicians !== null && (
              <ResultCard
                label={trans("analytics.requiredTechnicians")}
                value={simResult.requiredTechnicians}
                color="#181F67"
                divider
              />
            )}
          </div>

          {/* Recommendation */}
          {recommendation && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 16px",
                background: "#f0f3ff",
                borderRadius: "8px",
                borderLeft: "3px solid #181F67",
                fontSize: "13px",
                color: "#333",
                lineHeight: "1.5",
              }}
            >
              {recommendation}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ResultCard({ label, value, color, divider }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "14px 20px",
        background: "#f8f9fc",
        borderLeft: divider ? "1px solid #eef0f6" : "none",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          color: "#999",
          marginBottom: "6px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "22px", fontWeight: "700", color }}>{value}</div>
    </div>
  )
}

const filterColStyle = { display: "flex", flexDirection: "column", gap: "6px" }

export default OrdersInTime
